import React, { forwardRef, useId } from "react";
import { cn } from "../../lib/utils.js";
import { Button } from "../Button/Button.js";
import {
  BODY_FIT,
  BODY_INK,
  BUTTON_FIT,
  BUTTON_SIZE,
  FRAME,
  FRAME_PAINT,
  FRAME_SIZE,
  FRAME_SIZE_WITH_BODY,
  ICON_BOX,
  type ChipSize,
  type ChipTone,
} from "./chip-classes.js";
import type { ChipSelectButtonProps } from "./chip-props.js";
import { ChipRemoveButton } from "./ChipRemoveButton.js";

/* Chip — a compact pill: static, an action (onSelect), a toggle (onSelect +
   pressed), removable (onRemove), or an action beside a remove. Data-blind:
   the caller owns what selecting or removing means.

   - Action or toggle, not removable: the root IS a Button (outline, pill,
     xs at md and sm at lg), so tone, pressed and hover paint are Button's.
     pressed paints Button's teal selection. tone="ai" follows the
     toggle-group rule: an action chip (no pressed) and a pressed toggle are
     Button soft + ai, and an unpressed toggle rests untoned.
   - Static or removable: the root is FilterChip's pill frame (22px at md).
     With onSelect, a ghost pill Button body holds the content, and the
     frame is a role="group" named by the label, so the two buttons are
     siblings and never nest.
   - The remove control is ChipRemoveButton (internal): Button ghost
     iconOnly xs circle on the old 16px layout box, 44px under a coarse
     pointer.

   pressed is typed to need onSelect and to exclude onRemove. */

export type { ChipSize, ChipTone };

type ChipBase = Omit<
  React.HTMLAttributes<HTMLElement>,
  "onSelect" | "children"
> & {
  label: React.ReactNode;
  /** Optional value after the label (e.g. "Owner: Aria"). */
  value?: React.ReactNode;
  /** A leading decorative icon (aria-hidden). */
  icon?: React.ReactNode;
  /** Semantic colour (C7). Default "neutral": FilterChip's control frame. */
  tone?: ChipTone | undefined;
  /** md (default): 22px frame, 24px Button. lg: 28px for every form. */
  size?: ChipSize | undefined;
  disabled?: boolean | undefined;
};

export type { ChipSelectButtonProps };

/** Static, or removable with no action. */
type ChipStaticProps = ChipBase & {
  onSelect?: undefined;
  pressed?: undefined;
  /** When set, renders the remove (×) control. */
  onRemove?: (() => void) | undefined;
  /** Accessible name of the remove control (default "Remove {label}"). */
  removeLabel?: string | undefined;
  selectProps?: undefined;
};

/** An action, or with `pressed` a toggle. Never removable. */
type ChipSelectProps = ChipBase & {
  onSelect: () => void;
  /** Sets aria-pressed and paints the selection. */
  pressed?: boolean | undefined;
  onRemove?: undefined;
  removeLabel?: undefined;
  /** The root is the Button here, so its props go on the chip itself. */
  selectProps?: undefined;
};

/** An action beside a remove. Never a toggle. */
type ChipSelectRemoveProps = ChipBase & {
  onSelect: () => void;
  pressed?: undefined;
  onRemove: () => void;
  removeLabel?: string | undefined;
  /** ARIA and a ref for the body Button (see {@link ChipSelectButtonProps}). */
  selectProps?: ChipSelectButtonProps | undefined;
};

export type ChipProps =
  | ChipStaticProps
  | ChipSelectProps
  | ChipSelectRemoveProps;

export const Chip = forwardRef<HTMLElement, ChipProps>(function Chip(
  {
    label,
    value,
    icon,
    tone = "neutral",
    size = "md",
    disabled,
    onSelect,
    pressed,
    onRemove,
    removeLabel,
    selectProps,
    className,
    ...rest
  },
  ref,
) {
  const labelId = useId();
  const grouped = onSelect != null && onRemove != null;
  const onButton = onSelect != null;
  // An unpressed toggle rests untoned, so only it drops the ai paint.
  const aiPaint = tone === "ai" && pressed !== false;
  const quiet = !aiPaint && !pressed;
  const content = (
    <>
      {icon != null && (
        <span aria-hidden className={ICON_BOX[size]}>
          {icon}
        </span>
      )}
      <span id={grouped ? labelId : undefined} className="font-medium">
        {label}
      </span>
      {value != null && (
        <span
          className={
            cn(onButton && "font-normal", quiet && "text-pb-text-secondary") ||
            undefined
          }
        >
          {value}
        </span>
      )}
    </>
  );

  if (onSelect && !onRemove) {
    const shared = {
      ...rest,
      ref: ref as React.Ref<HTMLButtonElement>,
      shape: "pill",
      size: BUTTON_SIZE[size],
      pressed,
      disabled,
      onClick: () => onSelect(),
      className: cn(BUTTON_FIT[size], className),
      children: content,
    } as const;
    return aiPaint ? (
      <Button {...shared} variant="soft" tone="ai" />
    ) : (
      <Button {...shared} variant="outline" />
    );
  }

  return (
    <span
      ref={ref}
      role={grouped ? "group" : undefined}
      aria-labelledby={grouped ? labelId : undefined}
      className={cn(
        FRAME,
        onSelect ? FRAME_SIZE_WITH_BODY[size] : FRAME_SIZE[size],
        FRAME_PAINT[tone],
        disabled && "opacity-50",
        className,
      )}
      {...rest}
    >
      {onSelect ? (
        <Button
          {...selectProps}
          variant="ghost"
          shape="pill"
          size={BUTTON_SIZE[size]}
          disabled={disabled}
          onClick={() => onSelect()}
          className={cn(BODY_FIT[size], BODY_INK)}
        >
          {content}
        </Button>
      ) : (
        content
      )}
      {onRemove && (
        <ChipRemoveButton
          label={
            removeLabel ??
            (typeof label === "string" ? `Remove ${label}` : "Remove")
          }
          onRemove={onRemove}
          disabled={disabled}
        />
      )}
    </span>
  );
});
